import { getFileName } from '@core/core/helpers'
import type { OpenFilePickerConfigType } from '@core/core/modules'
import { useOpenFilePicker } from '@core/core/modules'
import {
  addMultipartFiles,
  addMultipartsToProject,
  addRemoteCrmsFiles,
  addRemoteGeoDbWells,
  deleteMultipartsToProject,
  getAllAvailableCompanies,
  selectActiveMultipartFile,
  selectActiveProject,
  selectMultipartUploadAll,
  setActiveMultipartFile,
  tryCancelUploadingFiles,
  updateMultipartReadyState,
  updateSetupConfig,
  updateUploadedFileStatus,
  updateUploadedFileStep,
  useAppDispatch,
  useAppSelector,
} from '@core/core/store'
import type {
  CrmsAttachment,
  KsuWell,
  MarkerSetupType,
  MultipartUploadedFile,
  MultipartUploadedFileSetupType,
  SegySetupType,
  UploadedFileEntityType,
  UploadedFileStatusType,
  UploadedFileStepTransitionType,
  UploadedFileStepType,
} from '@core/core/types'
import { nanoid } from '@reduxjs/toolkit'
import { useEffect, useMemo, useState } from 'react'

import { readSegyBlob } from '../MasterImport'
import type { MultipartUploadFileReturnType, UseMultipartUploadFilesReturnType } from '../types'

type OpenFile = { fileId: string; file?: File }
type UncompletedFiles = {
  fileId: string
  file?: File
}[]

export const useMultipartUploadFiles = (): UseMultipartUploadFilesReturnType => {
  const dispatch = useAppDispatch()
  const [openFiles, setOpenFiles] = useState<OpenFile[]>([])
  const storeFiles = useAppSelector(selectMultipartUploadAll)
  const activeFile = useAppSelector(selectActiveMultipartFile)
  const activeProject = useAppSelector(selectActiveProject)

  const [openedFiles, openedFilesOptions, getFilesFromExplorerByOption] = useOpenFilePicker()
  const getFilesFromExplorer = (options: OpenFilePickerConfigType = 'Survey3D') =>
    getFilesFromExplorerByOption(options)

  useEffect(() => {
    if (activeFile?.fileId && activeFile.step === 'ready') {
      dispatch(updateMultipartReadyState(activeFile.fileId))
    }
  }, [activeFile?.fileId, activeFile?.step])

  useEffect(() => {
    if (openedFiles.length) updateOpenFiles(openedFiles)
  }, [openedFiles])

  useEffect(() => {
    dispatch(getAllAvailableCompanies())
  }, [])

  const updateOpenFiles = (files: File[]) => {
    const newFiles = files
      .filter(
        file => !storeFiles.find(data => data.name === file.name && data.fileSize === file.size),
      )
      .map(file => ({
        fileId: nanoid(),
        file,
      }))
    if (newFiles.length > 0) {
      setOpenFiles(prevValue => [...prevValue, ...newFiles])
      Promise.all(
        newFiles.map(file =>
          convertToMultipartUploadedFile(
            file.fileId,
            file.file,
            activeProject.id,
            openedFilesOptions,
          ),
        ),
      ).then(files => {
        dispatch(addMultipartFiles(files))

        // Если не было, активных, активируем первый новый
        if (activeFile === undefined) {
          dispatch(setActiveMultipartFile(newFiles[0].fileId))
        }
      })
    }

    const uncompletedFiles: UncompletedFiles = storeFiles
      .filter(
        data =>
          files.find(file => data.name === file.name && data.fileSize === file.size) &&
          !openFiles.find(file => file.fileId === data.fileId),
      )
      .map(data => ({
        fileId: data.fileId,
        file: files.find(file => data.name === file.name && data.fileSize === file.size),
      }))
    setOpenFiles(prevValue => [...prevValue, ...uncompletedFiles])
    updateImportedFileStatus(uncompletedFiles)
  }

  const updateImportedFileStatus = (uncompleteFiles: UncompletedFiles) => {
    if (!openedFiles.length || !uncompleteFiles.length) {
      return
    }

    if (openedFiles.length === 1) {
      // Мы будем вручную обновлять статус загрузки, если либо выбрали активный файл и загружаем список, либо загружаем только этот один файл
      const currentUncompleteFile = uncompleteFiles.find(
        ({ file }) => file?.name === openedFiles[0].name && file.size === openedFiles[0].size,
      )
      // Если загрузили один, ищем этот файл среди старых
      if (currentUncompleteFile?.file) {
        dispatch(
          updateUploadedFileStatus(
            activeProject.id,
            currentUncompleteFile.fileId,
            'loading',
            'inprogress',
            currentUncompleteFile.file,
          ),
        )
      }
    } else {
      const currentUncompleteFile = uncompleteFiles.find(
        ({ file }) => file?.name === activeFile?.name && file?.size === activeFile?.fileSize,
      )
      // Если загружаем список файлов, ищем среди них активный и его статус обновляем
      if (currentUncompleteFile?.file) {
        dispatch(
          updateUploadedFileStatus(
            activeProject.id,
            currentUncompleteFile.fileId,
            'loading',
            'inprogress',
            currentUncompleteFile.file,
          ),
        )
      }
    }
  }

  const removeOpenFiles = (fileIds: string[], tasksIds?: string[]) => {
    setOpenFiles(openFiles.filter(file => !fileIds.includes(file.fileId)))
    dispatch(tryCancelUploadingFiles(tasksIds))
    dispatch(deleteMultipartsToProject(fileIds))
  }

  const updateActiveFile = (fileId?: string) => {
    dispatch(setActiveMultipartFile(fileId))
  }

  const updateFileStep = (fileId: string, step: UploadedFileStepTransitionType) => {
    const file = openFiles.find(file => file.fileId === fileId)?.file
    if (file) {
      dispatch(updateUploadedFileStep(activeProject.id, fileId, step, file))
    }
  }

  const updateFileStatus = (
    fileId: string,
    step: UploadedFileStepType,
    status: UploadedFileStatusType,
  ) => {
    const file = openFiles.find(file => file.fileId === fileId)?.file
    if (file) {
      dispatch(updateUploadedFileStatus(activeProject.id, fileId, step, status, file))
    }
  }

  const addFilesToProject = (fileIds: string[]) => {
    dispatch(addMultipartsToProject(activeProject.id, fileIds))
  }

  const updateFileConfig = (fileId: string, setupContent?: MultipartUploadedFileSetupType) => {
    const file = openFiles.find(file => file.fileId === fileId)?.file
    if (file) {
      dispatch(updateSetupConfig(fileId, setupContent))
    }
  }

  const addGeoDFiles = (files: KsuWell[]) => {
    dispatch(addRemoteGeoDbWells(activeProject.id, files))
  }

  const addCrmsFiles = (files: CrmsAttachment[]) => {
    dispatch(addRemoteCrmsFiles(activeProject.id, files))
  }

  const uploadedFiles: MultipartUploadFileReturnType[] = storeFiles.map(file => ({
    fileId: file.fileId,
    uploadedFile: file,
    file: openFiles.find(file => file.fileId === file.fileId)?.file,
  }))

  const activeUploadedFile: MultipartUploadFileReturnType = useMemo(
    () => ({
      fileId: activeFile?.fileId,
      uploadedFile: activeFile,
      file: activeFile
        ? openFiles.find(file => file.fileId === activeFile.fileId)?.file
        : undefined,
    }),
    [activeFile, openFiles],
  )

  return {
    uploadedFiles,
    removeOpenFiles,
    activeUploadedFile,
    updateActiveFile,
    updateFileStep,
    updateFileStatus,
    getFilesFromExplorer,
    updateFileConfig,
    addCrmsFiles,
    addGeoDFiles,
    addFilesToProject,
  }
}

const convertToMultipartUploadedFile = async (
  fileId: string,
  file: File,
  activeProjectId: string,
  openedFilesOptions: OpenFilePickerConfigType | undefined,
): Promise<MultipartUploadedFile> => {
  const getUploadedFile = (
    setupContent: MultipartUploadedFileSetupType | undefined,
    entityType: UploadedFileEntityType,
  ): MultipartUploadedFile => ({
    projectId: activeProjectId,
    fileId,
    name: file.name,
    uploadId: undefined,
    progress: 0,
    fileSize: file.size,
    fileChunk: 0,
    step: 'setup',
    setupContent,
    initialSetupContent: setupContent,
    date: file.lastModified,
    origin: 'localMine',
    entityType,
  })
  switch (openedFilesOptions) {
    case 'Markers': {
      const setupContent: MarkerSetupType = {
        missedLinesCount: 0,
        timeZone: '',
        separator: 'space',
        units: '',
        negativeZ: false,
        negativeTWT: false,
        markerNameColumn: 1,
        wellNameColumn: 1,
        encoding: 'cp1251',
      }
      return getUploadedFile(setupContent, 'marker')
    }
    default: {
      const setupContent: SegySetupType = {
        surveyName: getFileName(undefined, file.name),
        surveyType: 'Survey3D',
        domainType: 'TWT',
        ignoreSegy: false,
        check: false,
        ignoreZeros: true,
        samples: { scale: 1 },
        isValid: true,
      }
      const result = await readSegyBlob(file)
      const getSetupContent = (previousSetupContent: SegySetupType) => {
        const samples = { ...previousSetupContent.samples }
        const resultHeaderCoords = structuredClone(result.headersCoords)
        if (
          resultHeaderCoords?.x?.offset &&
          resultHeaderCoords.y?.offset &&
          resultHeaderCoords.iline?.offset &&
          resultHeaderCoords.xline?.offset
        ) {
          ++resultHeaderCoords.x.offset
          ++resultHeaderCoords.y.offset
          ++resultHeaderCoords.iline.offset
          ++resultHeaderCoords.xline.offset
        }

        const delayRecordingTime = result.delayRecordingTime ?? 0
        samples.startRecord ??= -delayRecordingTime
        // В segy сохраняется delayRecordingTime и оно имеет обратный знак к значению "Начало времени записи"
        // Поэтому пользователю будет отображаться обратное значение
        samples.interval ??= result.samplingStep
        samples.samplesPerTrace ??= result.samplesNumber
        samples.samplesFormat ??= result.formatCode

        return {
          ...previousSetupContent,
          samples,
          headerCoords: resultHeaderCoords,
          changed: true,
        }
      }

      return getUploadedFile(getSetupContent(setupContent), 'segy')
    }
  }
}
